import { useReducer, useEffect } from "react";

// Form components
import DatePicker from "../../components/forms/DatePicker";
import CheckSelect from "../../components/forms/CheckSelect";
import ContinentContainer from "./components/ContinentContainer";
import PersonCounter from "pages/Insurance-Info/components/PersonCounter";

// Variables/Config
import { worldContinents, travelTypes, sportActivities } from "./configuration";

// Services
import { DRPostGet } from "services/DRPostGetService";

// Redux
import { bundleActions } from "store/bundleData";
import { formValidityActions } from "store/formValidity";
import { formDataActions } from "store/formData";
import { useDispatch, useSelector } from "react-redux";

// -----------------------------------------------

// ? Reducer function
const formReducerFn = (state: any, action: any) => {
  switch (action.type) {
    case "update":
      return { ...state, [action.name]: action.payload };
    default:
      return state;
  }
};

const InfoCollect = () => {
  const { formData } = useSelector((state: any) => state.formData);

  // ! Reducer hook
  const [formReducerState, formReducerDispatch] = useReducer(
    formReducerFn,
    formData
  );

  // ! Redux
  const dispatch = useDispatch();

  // Dates
  let date = new Date();
  const dateToday = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .split("T")[0];
  const dateInXYears = new Date(date.setFullYear(date.getFullYear() + 2))
    .toISOString()
    .split("T")[0];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    dispatch(formDataActions.editFormData(formReducerState));
    if (
      // Dates validation
      formReducerState.departureDate !== "" &&
      formReducerState.departureDate >= dateToday &&
      formReducerState.departureDate <= dateInXYears &&
      formReducerState.arrivalDate !== "" &&
      formReducerState.arrivalDate >= dateToday &&
      formReducerState.arrivalDate <= dateInXYears &&
      // Checking if at least one continents is selected
      formReducerState.continents.length !== 0 &&
      // Checking if travel type is selected
      formReducerState.travelType !== "" &&
      // Checking if sport activitiy is selected
      formReducerState.sportActivities !== "" &&
      // People validation
      formReducerState.people.length !== 0 &&
      formReducerState.people.every(
        (ageItem: any) =>
          ageItem.age >= 0 &&
          ageItem.age !== false &&
          ageItem.age !== null &&
          ageItem.age !== undefined &&
          ageItem.age !== ""
      )
    ) {
      // DR API call (service)
      DRPostGet("travel-insurance-2vkyd1", formReducerState)
        .then((response) => {
          dispatch(formValidityActions.editFormValidity(true));
          dispatch(bundleActions.editBundleData(response[0].packages));
        })
        .catch((Err) => {
          dispatch(formValidityActions.editFormValidity(false));
          throw Error("DR API POST FAILED", Err);
        });
    } else {
      dispatch(formValidityActions.editFormValidity(false));
      console.log("PLEASE FILL ALL OF THE INPUTS");
    }
  }, [formReducerState, dispatch]);

  // ? Form item change handler
  const formItemChangeHandler = (payload: any, name: string) => {
    formReducerDispatch({
      type: "update",
      payload: payload,
      name: name,
    });
  };

  return (
    <form>
      <div className="container collect">
        <div className="collect__section">
          <h2 className="heading--secondary collect__heading">
            When are you going
          </h2>
          <p className="text--primary collect__text">
            Select the departure and arrival date of your trip.
          </p>
          <div className="input-control input-control--date">
            <DatePicker
              value={formReducerState.departureDate}
              onChange={(event: any) => {
                if (event.target.value > formReducerState.arrivalDate) {
                  let result = new Date(event.target.value);
                  result.setDate(result.getDate() + 7);
                  formReducerDispatch({
                    type: "update",
                    payload: result.toISOString().split("T")[0],
                    name: "arrivalDate",
                  });
                }
                formItemChangeHandler(event.target.value, event.target.name);
              }}
              minDateToday={true}
              max={dateInXYears}
              name="departureDate"
              label="Departure date"
              status={
                formReducerState.departureDate < dateToday ||
                formReducerState.departureDate > dateInXYears
                  ? "error"
                  : ""
              }
            />
            <DatePicker
              value={formReducerState.arrivalDate}
              onChange={(event: any) => {
                formItemChangeHandler(event.target.value, event.target.name);
              }}
              minDateToday={true}
              min={formReducerState.departureDate}
              max={dateInXYears}
              name="arrivalDate"
              label="Arrival date"
              status={
                formReducerState.arrivalDate < dateToday ||
                formReducerState.arrivalDate > dateInXYears
                  ? "error"
                  : ""
              }
            />
          </div>
        </div>
        <hr className="section-divider" />
        <div className="collect__section collect__section--continents">
          <h2 className="heading--secondary collect__heading">
            Where are you going
          </h2>
          <p className="text--primary collect__text">
            Select the specific continents you are going to visit or select the
            world option if you are going to travel all around the world.
          </p>
          <div className="input-control">
            <ContinentContainer
              value={formReducerState.continents}
              worldContinents={worldContinents}
              onChange={formItemChangeHandler}
            />
          </div>
        </div>
        <hr className="section-divider" />
        <div className="collect__section">
          <h2 className="heading--secondary collect__heading">
            What is your type of travel
          </h2>
          <p className="text--primary collect__text">
            The basic type of trip included in the travel insurance is
            tourism/vacation. You can then expand this basic type of trip to
            include administrative or manual work, or a study stay.
          </p>
          <div className="input-control">
            {travelTypes.map((travelType) => (
              <CheckSelect
                label={travelType.label}
                name={travelType.name}
                value={travelType.value}
                onChange={(event: any) => {
                  formItemChangeHandler(event.target.value, event.target.name);
                }}
                key={travelType.value}
                defaultChecked={
                  travelType.value === formReducerState.travelType
                }
              />
            ))}
          </div>
        </div>
        <hr className="section-divider" />
        <div className="collect__section">
          <h2 className="heading--secondary collect__heading">
            Which sports activities will you perform
          </h2>
          <p className="text--primary collect__text">
            The basic sports activities included in the travel insurance are
            recreational sports. You can then expand these basic sports
            activities with risk sports or winter sports.
          </p>
          <div className="input-control">
            {sportActivities.map((sportActivity) => (
              <CheckSelect
                label={sportActivity.label}
                name={sportActivity.name}
                value={sportActivity.value}
                onChange={(event: any) => {
                  formItemChangeHandler(event.target.value, event.target.name);
                }}
                key={sportActivity.value}
                defaultChecked={
                  sportActivity.value === formReducerState.sportActivities
                }
              />
            ))}
          </div>
        </div>
        <hr className="section-divider" />
        <div className="collect__section">
          <h2 className="heading--secondary collect__heading">
            How many people are going
          </h2>
          <p className="text--primary collect__text">
            Select the number and age of people participating in your trip.
          </p>
          <div className="input-control">
            <PersonCounter
              onChange={formItemChangeHandler}
              value={formReducerState.people}
            />
          </div>
        </div>
        <hr className="section-divider" />
        <div className="collect__section"></div>
      </div>
    </form>
  );
};

export default InfoCollect;
